import React, { useRef, useState } from 'react';
import NumberFormat from 'react-number-format';
import { UserDecimalSep } from 'models/user';
import { useUpdateEffect } from 'react-use';
import utils from 'utils';
import styled from 'styled-components/macro';
import { FeedbackText } from './TextInput';
import { useTheme } from 'providers/ThemeProvider';
import { sizePalette, fontSizePalette, fontWeightPalette, fontFamily } from 'utils/styleConstants';

interface StyleProps {
  hasFeedback?: boolean;
  feedbackColor?: string;
  width?: string;
  height?: string;
  border?: string;
  backgroundColor?: string;
  feedbackMessage?: string;
  fontSize?: string;
  padding?: string;
  fontColor?: string;
  fontWeight?: string;
}

interface TransientProps {
  $feedbackColor?: string;
  $hasFeedback?: boolean;
  $backgroundColor?: string;
  $fontColor?: string;
}

export interface NumberInputProps extends StyleProps, TransientProps {
  id?: string;
  decimalSeparator?: UserDecimalSep | boolean;
  onChange?: (value: number | undefined) => void;
  onFocus?: (value: number | undefined) => void;
  onBlur?: (value: number | undefined) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  value?: number | string;
  focus?: boolean;
  // always show decimals even if number is rounded
  hasFixedDecimals?: boolean;
  decimalScale?: number;
  isSearch?: boolean;
  readOnly?: boolean;
  customInput?: React.ComponentType<any>;

  saveAction?: (value: string | undefined) => void;
}

const SaveButton = styled.span`
  color: ${({ theme }) =>
    theme.colorPalette.isDarkMode ? theme.colorPalette.turquoise.light : theme.colorPalette.turquoise.dark};
  text-decoration: underline;
  font-size: ${fontSizePalette.small};
  font-weight: ${fontWeightPalette.medium};
  cursor: pointer;
  margin-left: ${sizePalette.spacing[2]};
`;

const StyledInput = styled(NumberFormat)<StyleProps & TransientProps>`
  border: ${sizePalette.borderWidth.DEFAULT} solid
    ${({ theme }) => (theme.colorPalette.isDarkMode ? theme.colorPalette.grey.grey3 : theme.colorPalette.grey.grey5)};
  border-radius: ${sizePalette.borderRadius.DEFAULT};
  transition: border-color ${sizePalette.transitionDuration[200]} ${sizePalette.transitionTimingFunction.out};

  font-family: ${fontFamily};
  font-size: ${({ fontSize }) => fontSize || fontSizePalette.small};
  font-weight: ${({ fontWeight }) => fontWeight || fontWeightPalette.regular};
  padding: ${({ padding }) => padding || sizePalette.spacing[2]};
  width: ${({ width }) => width || sizePalette.spacing.pxToRem(200)};
  height: ${({ height }) => height || sizePalette.spacing.pxToRem(30)};

  color: ${({ $fontColor, theme }) =>
    $fontColor || (theme.colorPalette.isDarkMode ? theme.colorPalette.grey.grey10 : theme.colorPalette.grey.grey9)};

  background-color: ${({ $backgroundColor, theme }) =>
    $backgroundColor || (theme.colorPalette.isDarkMode ? theme.colorPalette.grey.grey4 : theme.colorPalette.white)};

  ${({ border }) => border && `border: ${border};`}

  ${({ $hasFeedback, $feedbackColor }) =>
    $hasFeedback &&
    `
    border-color: ${$feedbackColor};
    box-shadow: 0 0 0 ${sizePalette.borderWidth[2]} ${$feedbackColor}${sizePalette.opacity[20]};
  `}

  ${({ readOnly, theme }) =>
    readOnly &&
    `
    color: ${theme.colorPalette.isDarkMode ? theme.colorPalette.grey.grey7 : theme.colorPalette.grey.grey5};
    background-color: ${theme.colorPalette.isDarkMode ? theme.colorPalette.grey.grey2 : theme.colorPalette.grey.grey1};
    cursor: not-allowed;
  `}

  &:focus {
    outline: none;
    border-color: ${({ theme }) =>
      theme.colorPalette.isDarkMode ? theme.colorPalette.turquoise.accent : theme.colorPalette.turquoise.dark};
  }

  &:focus-visible {
    ${({ border }) => border === 'none' && 'outline: none;'}
  }

  &:disabled {
    border-color: ${({ theme }) => (theme.colorPalette.isDarkMode ? 'transparent' : theme.colorPalette.grey.grey5)};
    color: ${({ theme }) => (theme.colorPalette.isDarkMode ? theme.colorPalette.black : theme.colorPalette.grey.grey7)};
    background-color: ${({ theme }) =>
      theme.colorPalette.isDarkMode ? theme.colorPalette.grey.grey1 : theme.colorPalette.grey.grey1};
    opacity: ${sizePalette.opacity[70]};
    cursor: not-allowed;
  }

  &:hover:not(:disabled):not(:focus) {
    border-color: ${({ theme }) =>
      theme.colorPalette.isDarkMode ? theme.colorPalette.grey.grey4 : theme.colorPalette.grey.grey7};
  }
`;

const NumberInput = (props: NumberInputProps) => {
  const { theme } = useTheme();
  const [isSaveActive, setIsSaveActive] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const {
    id = '',
    decimalSeparator = '.',
    decimalScale = 2,
    value,
    focus,
    hasFixedDecimals = false,
    readOnly = false,
    customInput,
    feedbackColor = theme.statusColors.error,
    feedbackMessage,
    hasFeedback = false,
    onChange,
    onFocus,
    onBlur,
    onKeyDown,
    height = sizePalette.spacing.pxToRem(30),
    border,
    backgroundColor,
    fontSize = fontSizePalette.medium,
    width = sizePalette.spacing.pxToRem(200),
    padding = sizePalette.spacing[2],
    fontColor,
    fontWeight = fontWeightPalette.regular,
    saveAction,
  } = props;

  useUpdateEffect(() => {
    if (inputRef.current && focus) {
      inputRef.current.focus();
    }
  }, [focus]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      if (e.target.value === '') {
        onChange && onChange(undefined);
      } else {
        onChange && onChange(utils.intl.convertToNumber(e.target.value));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleFocus = (e: React.ChangeEvent<HTMLInputElement>) => {
    saveAction && setIsSaveActive(true);
    try {
      if (!onFocus) return;
      if (e.target.value === '') {
        onFocus(undefined);
      } else {
        onFocus(utils.intl.convertToNumber(e.target.value));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTimeout(() => {
      saveAction && setIsSaveActive(false);
    }, parseInt(sizePalette.transitionDuration[500]));

    try {
      if (!onBlur) return;
      if (e.target.value === '') {
        onBlur(undefined);
      } else {
        onBlur(utils.intl.convertToNumber(e.target.value));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleSave = () => {
    saveAction && saveAction(inputRef?.current?.value);
  };

  return (
    <>
      <StyledInput
        id={id}
        customInput={customInput}
        value={value}
        decimalScale={decimalScale}
        fixedDecimalScale={hasFixedDecimals}
        getInputRef={inputRef}
        decimalSeparator={decimalSeparator}
        allowedDecimalSeparators={[',', '.']}
        readOnly={readOnly}
        disabled={readOnly}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={onKeyDown}
        $feedbackColor={feedbackColor}
        $hasFeedback={hasFeedback}
        height={height}
        border={border}
        width={width}
        $backgroundColor={backgroundColor}
        fontSize={fontSize}
        padding={padding}
        $fontColor={fontColor}
        fontWeight={fontWeight}
      />
      {isSaveActive && saveAction && <SaveButton onClick={handleSave}>Save</SaveButton>}
      {hasFeedback && feedbackMessage && <FeedbackText feedbackColor={feedbackColor}>{feedbackMessage}</FeedbackText>}
    </>
  );
};

export default NumberInput;
